import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { Col, Row, Space } from 'antd'
import { trim } from 'lodash'
import { selectCurrentUser } from 'Store/user/selector'
import { RootState } from 'Store'
import { connect } from 'react-redux'
import { appConfig } from 'config'
import { selectConfigList } from 'Store/pos/selector'
import { DocumentConfigKey, DOCUMENT_LOGO_POSITION } from 'helpers/constanst'
import { getConfigByKey } from 'helpers/format'

type Props = {
  companyInfo?: any
  leftInfo?: any
  leftInfoSpan?: number
  rightInfo?: any
  title?: string
  thead?: any
  tbody?: any
  summaryFooter?: any
  moreFooter?: any
  remarkFooter?: any
  footerSpan?: number
  signPosition?: string[]
  thead2?: any
  tbody2?: any
} & ReduxProps

const PdfPrintFormat: React.FunctionComponent<Props> = props => {
  const {
    companyInfo,
    leftInfo,
    rightInfo,
    title = '',
    thead,
    tbody,
    summaryFooter,
    footerSpan = 1,
    signPosition = [],
    moreFooter,
    remarkFooter,
    leftInfoSpan = 12,
    thead2,
    tbody2,
    currentUser,
    configList,
  } = props
  const logo = currentUser?.activeMerchant?.logo || ''
  const logoUrl = appConfig.assetCdnEndpoint + logo
  const configData = getConfigByKey(configList, DocumentConfigKey.COMMON)
  const config = configData && configData?.config_value && JSON.parse(configData?.config_value || '')
  const logoPosition = config?.logoPosition || DOCUMENT_LOGO_POSITION.L

  const renderTable = () => {
    return (
      <Table id="myTable">
        {thead}
        {tbody}
        {thead2 && tbody2 && (
          <>
            <br />
            {thead2}
            {tbody2}
            <br />
          </>
        )}
        <tfoot>
          {summaryFooter && (
            <tr>
              <td colSpan={footerSpan}>{summaryFooter}</td>
            </tr>
          )}
          {moreFooter && (
            <tr>
              <td colSpan={footerSpan}>{moreFooter} </td>
            </tr>
          )}
          {remarkFooter && (
            <tr>
              <td colSpan={footerSpan}>{remarkFooter} </td>
            </tr>
          )}
          {signPosition && signPosition.length > 0 && (
            <tr>
              <td colSpan={footerSpan}>
                <Row gutter={[16, 16]} justify={signPosition.length > 4 ? 'start' : 'space-around'}>
                  {signPosition.map((item: string, index: number) => {
                    return (
                      <Col span={6} key={index}>
                        <Space direction="vertical" align="center">
                          <InfoValue>{item}</InfoValue>
                          <br />
                          <Underline></Underline>
                          <Underline>วันที่</Underline>
                        </Space>
                      </Col>
                    )
                  })}
                </Row>
              </td>
            </tr>
          )}
        </tfoot>
      </Table>
    )
  }

  const renderHeader = () => {
    if (!config?.enableLogo) {
      return (
        <Row justify="space-between" gutter={[8, 8]} align="middle">
          <Col>{companyInfo}</Col>
        </Row>
      )
    }

    switch (logoPosition) {
      case DOCUMENT_LOGO_POSITION.L:
        return (
          <Row justify="start" gutter={[16, 8]} align="middle" wrap={false}>
            <Col>{<Logo src={logoUrl} />}</Col>
            <Col>{companyInfo}</Col>
          </Row>
        )
      case DOCUMENT_LOGO_POSITION.C:
        return (
          <Row justify="space-between" gutter={[8, 8]} align="middle">
            <Col span={10}>{companyInfo}</Col>
            <Col>{<Logo src={logoUrl} />}</Col>
            <Col span={10}></Col>
          </Row>
        )
      case DOCUMENT_LOGO_POSITION.R:
        return (
          <Row justify="space-between" gutter={[8, 8]} align="middle">
            <Col>{companyInfo}</Col>
            <Col>{<Logo src={logoUrl} />}</Col>
          </Row>
        )
    }

    return (
      <Row justify="start" gutter={[16, 8]} align="middle">
        <Col>{<Logo src={logoUrl} />}</Col>
        <Col>{companyInfo}</Col>
      </Row>
    )
  }

  return (
    <BoxWrapper className="landscape">
      {companyInfo && renderHeader()}
      {title && (
        <Row justify="center">
          <Col>
            <Title>{title}</Title>
          </Col>
        </Row>
      )}
      <br />
      <Row justify="space-between" gutter={[16, 16]}>
        {leftInfo && <Col span={leftInfoSpan}>{leftInfo}</Col>}
        {rightInfo && <Col>{rightInfo}</Col>}
      </Row>
      <Row>
        <Col span={24}>{renderTable()}</Col>
      </Row>
    </BoxWrapper>
  )
}

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.h3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  margin: 5px 0 0 0;
`

const BoxWrapper = styled.div`
  width: 0;
  height: 0;
  overflow: hidden;
  background-color: #fff;
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.medium};

  @media print {
    width: 100%;
    height: 100%;
    overflow: visible;

    @page {
      padding: 1cm 1cm 1.25cm 1.25cm;
      margin: 0.25cm;
      vertical-align: top;
    }

    .portrait @page {
      width: 21cm;
      min-height: 29.7cm;
    }

    .landscape @page {
      width: 29.7cm;
      min-height: 14.8cm;
    }

    table {
      page-break-after: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    td {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group;
    }
    tfoot {
      display: contents;
    }
  }

  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const InfoValue = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  margin-right: 10px;
`

const Underline = styled.div`
  text-align: left;
  width: 150px;
  border-bottom: 1px solid;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const Table = styled.table`
  width: 100%;
  margin: 20px 0;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  th,
  td {
    border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.black};
    padding: 10px 5px;
    vertical-align: top;
    text-align: center;
  }
  th {
    font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.regular};
    vertical-align: middle;
    background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
    font-weight: 100;
  }

  .right {
    text-align: right;
  }
  .left {
    text-align: left;
  }
  .title {
    font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
    text-align: left;
  }
`

const Logo = styled.img`
  //filter: grayscale(100%);
  height: 80px;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    configList: selectConfigList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(PdfPrintFormat)
